import { TFilterStatusEnum } from '~/utils/Enum';
import {
  ConversationStatusEnum,
  IAttendanceConfirmation,
  IClockInformation,
  ICounterOffer,
  IFavoriteInvite,
  IJobAdjustment,
  IMessage,
  IRegularAndOvertimeHours,
  ITransaction,
  IUrgentShift,
  IUser,
  IUserNote,
  SkillsEnum,
  UserTypeEnum,
  WorkerClassificationTypeEnum,
} from '.';
import { TCamelCasedProperties } from '../utils/CamelCase';
import { Mapper } from '../utils/Mapper';

export interface IJob {
  readonly id: string;
  readonly createdAt: Date;
  readonly offer_owner: IUser;
  readonly offer_final_reciever: IUser;
  readonly payoutMethodChosen: boolean;
  readonly rate: number;
  readonly original_rate: number;
  readonly lunch_break_time: string;
  readonly original_lunch_break_time: string;
  readonly lunch_final_break_time: string;
  readonly is_lunch_break_paid: boolean;
  readonly job_status: JobStatusEnum;
  readonly job_filled_datetime: Date | null;
  readonly job_datetime: Date;
  readonly original_job_datetime: Date;
  readonly job_datetime_end: Date;
  readonly original_job_datetime_end: Date;
  readonly prefer_type: string;
  readonly offer_unavailable: IUser[];
  readonly offer_ignored: IUser[];
  readonly offer_seen: IUser[];
  readonly notes_for_temp: string;
  readonly rating: number;
  readonly transaction_id: string;
  readonly repost: boolean;
  readonly repostCount: number;
  readonly adjustments: IJobAdjustment[];
  readonly offer_counters: ICounterOffer[];
  readonly state: string;
  readonly timezone: string;
  readonly profession: string;
  readonly professionName: string;
  readonly subProfession: string | null;
  readonly specializations: SpecializationsEnum[] | null;
  readonly skills: IJobSkill[];
  readonly specialty: string;
  readonly reachout_date: Date;
  readonly is_reachout_done: boolean;
  readonly is_conversation_open: boolean;
  readonly conversation_status: ConversationStatusEnum;
  readonly office_last_read_time: Date;
  readonly professional_last_read_time: Date;
  readonly multi_day_booking: string;
  readonly available_users: IUser[];
  readonly ram: IUser;
  readonly dsoManager: IUser;
  readonly messages: IMessage[];
  readonly is_conversation_support_requested: boolean;
  readonly job_cancelled_datetime: Date;
  readonly is_cancelled_by_user_type: UserTypeEnum;
  readonly is_cancelled: boolean;
  readonly cancelled_by?: IUser;
  readonly cancellationReason?: string | null;
  readonly cancellationFeedback?: string | null;
  readonly cancelPerformedBy: IUser;
  readonly receipt: string;
  readonly stripe_invoice_id: string;
  readonly pay_out_transfer_id: string;
  readonly transactions: ITransaction[];
  readonly paid_out: boolean;
  readonly disabled_payout: boolean;
  readonly coms_session_id: string;
  readonly twilio_dentist_sid: string;
  readonly twilio_hygienist_sid: string;
  readonly twilio_session_expiration: Date;
  readonly patientsPerHour: string;
  readonly workTime: number;
  readonly date_till_unavailable: Date;
  readonly totalAmounts: { regular: number; overtime: number };
  readonly notes: IUserNote[];
  /**
   * Who posted the shift (GOTU-8131): the account holder's first and last name,
   * never `office_name`; admins arrive masked as "GoTu Admin". `null` when the
   * backend cannot name anyone. Payloads before release 1.230.0 omitted the key.
   */
  readonly author: Pick<IUser, '_id' | 'first_name' | 'last_name'> | null;
  readonly favoriteInvites: IFavoriteInvite[];
  readonly confirmPerformedBy: IUser;
  readonly reportNoShow: boolean;
  readonly autoFill: boolean;
  readonly isAutoFilled: boolean;
  readonly isAutoRefundProcessed: boolean;
  readonly isAutoRefundEnabled: boolean;
  readonly workerClassification: WorkerClassificationTypeEnum | null;
  readonly expressPay: ExpressPayStatusEnum;
  readonly stopCharges: boolean;
  readonly confirmAttendance: IAttendanceConfirmation;
  readonly clockInOut: IClockInformation;
  readonly dualModelEnabled: boolean;
  readonly regularAndOvertimeHours: IRegularAndOvertimeHours;
  readonly rateHour: IRegularAndOvertimeHours;
  readonly incident: JobIncidentEnum | boolean;
  readonly isUrgent: boolean;
  readonly urgentShift: IUrgentShift | null;
  readonly stopInvoices: boolean;
  readonly status: JobStatusEnum;
  readonly tm_fee: number;
  readonly tm_fee_percent: number;
  readonly total: number;
  readonly totalHourls: number;
  readonly totalHours: number;
  readonly totalOvertime: number;
  readonly totalRegular: number;
  readonly totalTransactionAmount: number;
  readonly total_refund: number;
  readonly workingHours: number;
  readonly _id: string;
  readonly amount: number;
  readonly cancellation_fee: number;
  readonly candidateRate: number | null;
  readonly card_name: string;
  readonly card_number: string;
  readonly convenience_fee: number;
  readonly convenience_fee_percent: number;
  readonly distance: number | null;
  readonly employeeOptionFee: number;
  readonly employeeOptionFeePercent: number;
  readonly extraPay: number;
  /**
   * Phase-1 office promo discount (GOTU-5659). Served by the backend only when
   * its own gate is on for the office AND the amount is > 0, so the FE renders
   * on presence alone. The service fee stays FULL and only `total` is reduced,
   * so this row is load-bearing for the breakdown to add up. Carries no `code`
   * at this level.
   */
  readonly promoDiscount?: { amount: number };
  readonly isAdjusted: boolean;
  readonly isFeedbackSubmited: boolean;
  readonly local_date: string;
  readonly local_end_time: string;
  readonly local_start_time: string;
  readonly local_timezone: string;
  readonly updatedAt: Date;
  readonly workingHourls: number;
  readonly isAssistedHygiene: boolean;
  /**
   * GoTu-funded credit that covers the gap on an accepted counter offer. Served
   * by `/jobs/:jobId` and rendered as a receipt credit; optional because only
   * counter-covered shifts carry it.
   */
  readonly counterCover?: ICounterCover | null;
  readonly parking_info: string;
  readonly multipleCounters: IMultipleCounters[];
  readonly isOnsiteInterview: boolean;
  readonly postingId: string;
  readonly postingTitle?: string;
}

export interface IMultipleCounters {
  readonly id: string;
  readonly candidate: IUser;
  readonly requestor: IUser;
  readonly candidateRate: number;
  readonly distance: number;
  readonly expiration: Date;
  readonly isFavorite: boolean;
  readonly originalRate: number;
  readonly begin: Date;
  readonly end: Date;
  readonly originalBegin: Date;
  readonly originalEnd: Date;
  readonly breakTime: string;
  readonly originalBreakTime: string;
  readonly rate?: number;
  readonly score?: {
    readonly value: number;
    readonly loyaltyScore?: number;
  };
  readonly local_begin: string;
  readonly local_end: string;
  readonly hasWorkedWithDSO: boolean;
  readonly hasWorkedWithOffice: boolean;
  /** Count of other live offers the candidate holds whose shift time overlaps this job. */
  readonly competingOffersCount?: number;
  readonly payment?: {
    readonly convenienceFee?: number;
    readonly convenienceFeeRatio?: number;
    readonly hygienistPay?: number;
    readonly peoFee?: number;
    readonly peoFeeRatio?: number;
    readonly tempmeeFee?: number;
    readonly tempmeeFeeRatio?: number;
    readonly totalAmount?: number;
    /**
     * Phase-1 office promo discount (GOTU-5659) from GET /jobs/:jobId/candidates.
     * `totalAmount` is already net of `promoDiscount.amount`; `code` is always ''
     * in phase 1 (automatic office/state discounts) and must not be rendered —
     * the promo-code apply flow is phase 2.
     */
    readonly promoDiscount?: { code: string; amount: number };
  };
}

export interface ICountAndDatesJob {
  readonly autoFill: boolean;
  readonly date: string;
  readonly dateEnd: string;
  readonly id: string;
  readonly localDate: string;
  readonly offerEnds: string;
  readonly offerStarts: string;
  readonly profession: string;
  readonly status?: TFilterStatusEnum;
  readonly professional: {
    readonly full_name: string;
    readonly profile_image: string;
    readonly _id: string;
  }[];
  readonly subProfession: string | null;
}

export enum JobStatusEnum {
  All = 'all',
  Active = 'active',
  Adjusted = 'adjusted-required',
  Cancelled = 'cancelled',
  Filled = 'filled',
  AllFilled = 'all-filled',
  Pending = 'pending',
  Open = 'open',
  Complete = 'complete',
  Counter = 'counter',
  Expired = 'expired',
  PayOut = 'pay-out',
  Past = 'past',
  PendingCounter = 'pending-counter',
  Refill = 'refill',
  CancelledHyg = 'cancelled-hyg',
  CancelledDnt = 'cancelled-dnt',
  PayOutDisabled = 'pay-out-disabled',
  RefundDisabled = 'refund-disabled',
}

export enum VirtualJobStatusEnum {
  All = 'all',
  Active = 'active',
  Adjusted = 'adjusted-required',
  Cancelled = 'cancelled',
  Filled = 'filled',
  AllFilled = 'all-filled',
  Pending = 'pending',
  Open = 'open',
  Complete = 'complete',
  Counter = 'counter',
  Expired = 'expired',
  PayOut = 'pay-out',
  Past = 'past',
  PendingCounter = 'pending-counter',
  Refill = 'refill',
  CancelledHyg = 'cancelled-hyg',
  CancelledDnt = 'cancelled-dnt',
}

export enum SpecializationsEnum {
  General = 'general',
  Surgical = 'surgical',
  Orthodontics = 'orthodontics',
  Pediatrics = 'pediatrics',
  Endodontics = 'endodontics',
  Oralsurgery = 'oralsurgery',
  Periodontics = 'periodontics',
  EFDA = 'efda',
  RDA = 'rda',
  Radiology = 'radiology',
  Prosthodontics = 'Prosthodontics',
  HygieneAssistant = 'Hygiene Assistant',
  Other = 'other',
}

export interface IReceipt {
  type: string;
  paid: boolean;
  _id: string;
  status: string;
  amount: number;
  transaction_id?: string;
  description?: string;
  label: string;
  payment_method: {
    type?: string;
    last4?: string;
    brand?: string;
  };
  components: {
    _id: string;
    label: string;
    percent: number;
    amount: number;
  }[];
  date: Date;
  id: string;
  jobId: string;
  jobDate: string;
  offer_owner: {
    office_name: string;
    id: string;
  };
  counterCover: ICounterCover | null;
}

export enum CounterCoverStatusEnum {
  pending = 'pending',
  applied = 'applied',
}

export interface ICounterCover {
  _id: string;
  createdAt: Date;
  author: IUser;
  status: CounterCoverStatusEnum;
  amount: number;
}

interface IJobSkill {
  readonly code: SkillsEnum;
}

export interface IJobsWithTransaction {
  id: string;
  shift_date: Date;
  no_transactions: number;
  shift_status: string;
  offer_final_reciever?: string;
  offer_owner: {
    full_name: string;
    office_name: string;
  };
  employeeOptionFee: number;
  isOnsiteInterview: boolean;
}

export interface IInvoice {
  transaction_id: string;
  job_id: string;
  paid: boolean;
  amount: number;
  created_date: Date;
  payment_date: Date;
  jobDate: Date;
  date: string;
  office_name: string;
  office_id: string;
  invoice_url?: string;
  invoice_number?: string;
  isOnsiteInterview?: boolean;
  paymentRecordType: PaymentRecordType;
}

export enum PaymentRecordType {
  OnsiteInterview = 'onsite_interview',
  Shift = 'shift',
  ListingFee = 'listing_fee',
}

export enum ExpressPayStatusEnum {
  NotRequested = 'not_requested',
  Requested = 'requested',
  Waived = 'waived',
  NotUsed = 'not_used',
}

export enum JobIncidentEnum {
  NoShow = 'no-show',
  WalkedOut = 'walked-out',
}

export type TJob = TCamelCasedProperties<IJob>;

export class JobMapper {
  readonly job: IJob;

  private constructor(job: IJob) {
    this.job = job;
  }

  static of(job: IJob): JobMapper {
    return new JobMapper(job);
  }

  toDomain(): TJob {
    return Mapper.toCamelCase<TJob>(this.job);
  }

  static toPersistenceOf(job: TJob): IJob {
    return Mapper.toSnakeCase<IJob>(job);
  }

  get createdAt(): Date | null {
    return this.job.createdAt ? new Date(this.job.createdAt) : null;
  }
}
